import {useState} from 'react';
import {Alert, Button, Code, List, Modal, Stack, Text, TextInput} from '@mantine/core';
import {AiOutlineDelete, AiOutlineWarning} from 'react-icons/ai';
import {Application} from '../types';
import {getDecommissionEligibility} from '../utils/decommissionEligibility';

// FNP-998: decommission a provisioned application.
//
// Owner requirement (2026-08-16): "I should be able to decommission
// applications that have previously been commissioned."
//
// ⚠️ THIS DESTROYS REAL INFRASTRUCTURE and cannot be undone. Unlike deleting
// the record — which removes a row — this tears down the S3 bucket, the
// CloudFront distribution, the DNS record, both pipelines and both
// repositories. The site stops serving.
//
// The confirmation therefore requires TYPING THE APPLICATION NAME rather than
// clicking through a dialog. A misclick on a list row is a plausible accident;
// typing the name of the wrong application is not.
//
// The button also refuses up front when the record has no stack name for the
// target stage, because the flow would refuse anyway — better to say so here,
// next to the Provisioning tab where it can be fixed, than to start a run that
// fails at its first step.
//
// ═══ FNP-1014: THE MODAL MUST NAME EVERY STACK, NOT ONE ═══
//
// Decommissioning is application-level, not stage-scoped. The repos and
// pipelines are SHARED across stages — exactly two repos and two pipelines
// exist per application, not six — so tearing them down ends every stage at
// once, and the server deletes every recorded stack rather than orphaning the
// ones it did not name.
//
// This modal used to list a single `{stackName}` under "What will be deleted".
// Under the §7 pipeline ladder a fully-provisioned application legitimately
// carries THREE stacks, so the operator was typing the application's name to
// confirm a blast radius a third the size of the real one. A typed-name
// confirmation is only meaningful if what is being confirmed is stated
// accurately, so the list below enumerates all of them.

interface Props {
    record: Application;
    stage: 'dev' | 'test' | 'prod';
    onDecommission: () => void;
    disabled?: boolean;
}

export const DecommissionButton = (
    {
        record,
        stage,
        onDecommission,
        disabled = false,
    }: Props) => {

    const [opened, setOpened] = useState(false);
    const [typedName, setTypedName] = useState('');

    const applicationName = (record.name as string) ?? '';

    // Shared with the tests (utils/decommissionEligibility.ts) so the rule the
    // UI enforces and the rule under test cannot drift apart. Mirrors the
    // server-side gate; the server refuses regardless, but a UI that starts a
    // run doomed to fail at step one is worse than one that explains the
    // problem next to the tab that fixes it.
    // ⚠️ The gate is UNCHANGED and still keyed on this console's own stage: a
    // record with no stack name for it has never been through the persisting
    // orchestrator, and the server refuses such a teardown. `eligibility`'s own
    // `stackName` is no longer rendered — the list below enumerates every
    // stack instead — but the refusal it drives is exactly as before.
    const eligibility = getDecommissionEligibility(record, stage);
    const missingStackName = eligibility.blocker === 'NO_STACK_NAME';

    // FNP-1014: every stack this teardown will delete, in stage order.
    //
    // Stages the orchestrator never deployed carry '' rather than being absent
    // — the API's StageStringMap declares all three keys non-null, so "not
    // provisioned" has to be encoded rather than omitted — hence the filter.
    // This mirrors `deprovisionTargets` in console-api's
    // validate-deprovisionable.ts, which is what the server actually iterates.
    const targets = (['dev', 'test', 'prod'] as const)
        .map((s) => ({stage: s, stackName: record.infraStackNames?.[s] ?? ''}))
        .filter((t) => t.stackName !== '');
    const alreadyHandled = eligibility.blocker === 'ALREADY_DELETING'
        || eligibility.blocker === 'ALREADY_DECOMMISSIONED';

    const nameMatches = typedName.trim() === applicationName;

    const close = () => {
        setTypedName('');
        setOpened(false);
    };

    return (
        <>
            <Button
                variant='outline'
                color='red'
                leftSection={<AiOutlineDelete/>}
                disabled={disabled || alreadyHandled}
                onClick={() => setOpened(true)}
            >
                Decommission
            </Button>

            <Modal
                opened={opened}
                onClose={close}
                title={`Decommission ${applicationName}`}
                size='lg'
            >
                <Stack gap='md'>
                    {missingStackName ? (
                        <Alert
                            variant='light'
                            color='orange'
                            icon={<AiOutlineWarning/>}
                            title='This application cannot be decommissioned yet'
                        >
                            <Text size='sm'>
                                No infrastructure stack name is recorded for the{' '}
                                <strong>{stage}</strong> environment, so the platform does not
                                know which stack to delete. It will not guess: a stack it cannot
                                find looks exactly like one that has already been deleted, and
                                acting on that would report success while the application kept
                                running.
                            </Text>
                            {/*
                              * FNP-1007: this advice USED TO BE A DEAD END.
                              *
                              * It said only "add the stack name on the Provisioning
                              * tab" — which is impossible for an orphaned record.
                              * Six ACTIVE applications own no infrastructure at all
                              * (torn down by hand, record left behind), so there is
                              * no stack name in existence to add. Combined with the
                              * Edit form's Status radio offering only ACTIVE and
                              * INACTIVE (FNP-1041), those records were unreachable
                              * through the product entirely.
                              *
                              * There are now TWO paths, and the copy has to name
                              * both, because which one applies depends on a fact
                              * the admin holds and this dialog cannot see: whether
                              * the infrastructure still exists.
                              */}
                            <Text size='sm' mt='xs'>
                                There are two ways forward, depending on whether this
                                application&apos;s infrastructure still exists:
                            </Text>
                            <List size='sm' spacing='xs' mt='xs'>
                                <List.Item>
                                    <strong>It still exists</strong> — add the stack name on the{' '}
                                    <strong>Provisioning</strong> tab, then come back here to tear
                                    it down.
                                </List.Item>
                                <List.Item>
                                    <strong>It is already gone</strong> — for example it was
                                    removed by hand and only this record was left behind — there
                                    is no stack name to add. Close this and use{' '}
                                    <strong>Mark as decommissioned</strong>, which checks that
                                    nothing is left and then corrects the record.{' '}
                                    <Text size='sm' span c='dimmed'>
                                        It deletes nothing.
                                    </Text>
                                </List.Item>
                            </List>
                        </Alert>
                    ) : (
                        <>
                            <Alert
                                variant='light'
                                color='red'
                                icon={<AiOutlineWarning/>}
                                title='This cannot be undone'
                            >
                                <Text size='sm'>
                                    Decommissioning permanently deletes this application&apos;s
                                    infrastructure. The site stops serving immediately and the
                                    content cannot be recovered — it would have to be
                                    re-provisioned and redeployed from source.
                                </Text>
                            </Alert>

                            {targets.length > 1 && (
                                <Alert
                                    variant='light'
                                    color='red'
                                    icon={<AiOutlineWarning/>}
                                    title={`This affects all ${targets.length} environments`}
                                >
                                    <Text size='sm'>
                                        Decommissioning is not limited to one environment. This
                                        application&apos;s pipelines and repositories are shared
                                        across every environment it is deployed to, so tearing
                                        them down ends <strong>all of them</strong> — every stack
                                        listed below is deleted.
                                    </Text>
                                </Alert>
                            )}

                            <div>
                                <Text size='sm' fw={500} mb='xs'>What will be deleted:</Text>
                                <List size='sm' spacing='xs'>
                                    {/*
                                      * FNP-1014: one entry per recorded stack. This used to name
                                      * a single stack while the teardown deleted every one of
                                      * them, so the typed-name confirmation below was given
                                      * against a fraction of the real blast radius.
                                      */}
                                    {targets.map((t) => (
                                        <List.Item key={t.stage}>
                                            Infrastructure stack <Code>{t.stackName}</Code>{' '}
                                            (<strong>{t.stage}</strong>) — the S3 bucket,
                                            CloudFront distribution and DNS record
                                        </List.Item>
                                    ))}
                                    <List.Item>
                                        Both managed pipelines
                                    </List.Item>
                                    <List.Item>
                                        Both source repositories
                                    </List.Item>
                                </List>
                            </div>

                            <div>
                                <Text size='sm' fw={500} mb='xs'>What is kept:</Text>
                                <Text size='sm' c='dimmed'>
                                    The application record, renamed to{' '}
                                    <Code>{applicationName}-decommissioned-&lt;date&gt;</Code> so
                                    its history is preserved and the name{' '}
                                    <Code>{applicationName}</Code> becomes available again.
                                </Text>
                            </div>

                            <TextInput
                                label={`Type ${applicationName} to confirm`}
                                placeholder={applicationName}
                                value={typedName}
                                onChange={(e) => setTypedName(e.currentTarget.value)}
                                data-testid='decommission-confirm-input'
                            />
                        </>
                    )}

                    <Stack gap='xs'>
                        <Button
                            color='red'
                            disabled={missingStackName || !nameMatches}
                            onClick={() => {
                                onDecommission();
                                close();
                            }}
                        >
                            Decommission this application
                        </Button>
                        <Button variant='subtle' onClick={close}>Cancel</Button>
                    </Stack>
                </Stack>
            </Modal>
        </>
    );
};
